"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  eyebrow: "REVENUE GENERATED",
  value: 48200000,
  suffix: "+",
  caption: "Total client growth driven by our high-velocity performance marketing.",
  fill: "hsl(240 6% 10%)"
}, {
  eyebrow: "CAMPAIGNS LAUNCHED",
  value: 1200,
  suffix: "+",
  caption: "Precision-engineered digital strategies that disrupt the market.",
  fill: "hsl(240 6% 10%)"
}, {
  eyebrow: "CLIENT RETENTION",
  value: 98,
  suffix: "%",
  caption: "Partners who trust our data-driven approach for long-term scale.",
  fill: "hsl(240 6% 10%)"
}, {
  eyebrow: "MARKETS DOMINATED",
  value: 27,
  suffix: "",
  caption: "Global territories where our clients lead their respective industries.",
  fill: "hsl(240 6% 10%)"
}];
function formatNumber(n: number, target: number): string {
  if (target >= 1000000) {
    return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (target >= 1000) {
    return Math.floor(n).toLocaleString("en-US");
  }
  return Math.floor(n).toString();
}
function AnimatedNumber({
  target,
  suffix,
  start
}: {
  target: number;
  suffix: string;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const duration = 1600;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target]);
  return <span className="tabular-nums">
      {formatNumber(value, target)}
      {suffix}
    </span>;
}
export default function LiveImpactTracker() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: scribbleRef,
    isVisible: scribbleVisible
  } = useRevealOnScroll<SVGSVGElement>({
    threshold: 0.4
  });
  return <section data-section-id="1312" id="live-impact-tracker" className="relative overflow-hidden py-28 md:py-40 bg-background text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <span className="font-default leading-none" style={{
        color: "hsl(22 100% 50%)",
        opacity: 0.03,
        fontSize: "clamp(20rem, 40vw, 36rem)",
        fontWeight: 800
      }}>
          #
        </span>
      </div>

      <div ref={sectionRef} className="relative mx-auto max-w-[1440px] px-6 md:px-12" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(16px)",
      transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)"
    }}>
        {}
        <div className="mx-auto max-w-[720px] text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="font-mono text-[11px] sm:text-xs md:text-[13px] font-medium uppercase" style={{
            color: "hsl(22 100% 50%)",
            letterSpacing: "0.2em"
          }}>
              VOL. 01 // IMPACT METRICS
            </span>
            <span aria-hidden="true" className="inline-block h-2 w-2" style={{
            backgroundColor: "hsl(22 100% 50%)"
          }} />
          </div>

          <h2 className="font-default mt-6 font-extrabold uppercase" style={{
          color: "hsl(0 0% 98%)",
          fontSize: "clamp(2rem, 4.4vw, 3.75rem)",
          letterSpacing: "-0.04em",
          lineHeight: 1.0
        }}>
            Data-driven.{" "}
            <span className="relative inline-block">
              
              <svg ref={scribbleRef} aria-hidden="true" viewBox="0 0 120 70" className="absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2" style={{
              width: "130%",
              height: "140%",
              transform: "translate(-50%, -50%) rotate(-2deg)",
              overflow: "visible"
            }}>
                <path d="M 60 8 C 90 8, 112 22, 112 35 C 112 50, 88 62, 60 62 C 30 62, 8 50, 8 35 C 8 20, 32 8, 60 8 Z" fill="none" stroke="hsl(22 100% 50%)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="320" strokeDashoffset={scribbleVisible ? 0 : 320} style={{
                transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)"
              }} />
              </svg>
            </span>{" "}
            accelerated.
          </h2>

          <p className="font-default mt-5 mx-auto max-w-[560px] text-base md:text-lg" style={{
          color: "hsl(240 5% 65%)",
          lineHeight: 1.6
        }}>
            We transform digital presence into measurable revenue through high-velocity performance marketing.
          </p>
        </div>

        {}
        <div className="relative mt-16 md:mt-20">
          {}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => <div key={i} data-index={i} className="relative" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)",
            transition: `opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 80}ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 80}ms`
          }}>
                {}
                <div className="relative p-7 md:p-8 h-full flex flex-col" style={{
              backgroundColor: stat.fill,
              border: "1px solid hsl(240 5% 15%)"
            }}>
                  <div className="font-mono text-[11px] font-medium uppercase" style={{
                color: "hsl(22 100% 50%)",
                letterSpacing: "0.2em"
              }}>
                    {stat.eyebrow}
                  </div>

                  <div className="font-default mt-4 font-extrabold leading-none" style={{
                color: "hsl(0 0% 98%)",
                fontSize: "clamp(3rem, 7vw, 4rem)",
                letterSpacing: "-0.04em"
              }}>
                    <AnimatedNumber target={stat.value} suffix={stat.suffix} start={isVisible} />
                  </div>

                  <p className="font-default mt-4 text-sm" style={{
                color: "hsl(240 5% 65%)",
                lineHeight: 1.5
              }}>
                    {stat.caption}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}